import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { streakService } from '../services/streakService';

const StudyContext = createContext();

export const useStudy = () => {
  return useContext(StudyContext);
};

export const StudyProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [studyData, setStudyData] = useState({
    streakData: { currentStreak: 0, longest: 0 },
    studyHistory: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const updateDailyStreak = async () => {
    try {
      if (!currentUser) return;
      
      const streak = await streakService.recordDailyVisit(currentUser.uid);
      const streakData = await streakService.getStreak(currentUser.uid);
      
      setStudyData(prev => ({
        ...prev,
        streakData: {
          currentStreak: streak,
          longest: streakData.longest || streak,
          lastStudyDate: new Date()
        }
      }));

    } catch (error) {
      console.error('Error updating daily streak:', error);
    }
  };

  const loadStudyData = async () => {
    if (!currentUser) {
      console.log('No current user, resetting study data');
      setStudyData({
        streakData: { currentStreak: 0, longest: 0, lastStudyDate: null },
        studyHistory: []
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('Loading streak data for user:', currentUser.uid);
      
      // Load streak data from streakService
      const streakData = await streakService.getStreak(currentUser.uid);
      
      console.log('Loaded streak data:', streakData);

      // Load study history
      const historyQuery = query(
        collection(db, 'studyHistory'),
        where('userId', '==', currentUser.uid),
        where('type', '==', 'daily_visit'),
        orderBy('timestamp', 'desc'),
        limit(30)
      );
      
      console.log('Fetching study history...');
      const historySnapshot = await getDocs(historyQuery);
      const studyHistory = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Loaded study history:', studyHistory.length, 'entries');

      setStudyData({
        studyHistory,
        streakData: {
          currentStreak: streakData.current,
          longest: streakData.longest,
          lastStudyDate: streakData.lastActivity?.toDate() || null
        }
      });

      console.log('Updating daily streak...');
      await updateDailyStreak();
      console.log('Daily streak updated successfully');
    } catch (err) {
      console.error('Error loading study data:', err);
      setError('Failed to load study data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load study data when user changes
  useEffect(() => {
    loadStudyData();
  }, [currentUser]);

  const refreshStudyData = async () => {
    await loadStudyData();
  };

  const value = {
    studyData,
    loading,
    error,
    refreshStudyData
  };

  return (
    <StudyContext.Provider value={value}>
      {children}
    </StudyContext.Provider>
  );
};

export default StudyContext;